import {
  CalendarGridDayOverlappedTimeLineItem,
  CalendarGridDayTimeLineItem,
  CalendarGridTimeMode,
  CalendarGridTimeString,
  OnOverlappedTimeLineItemProps,
} from '../../types';

const toMinutes = (time: CalendarGridTimeString): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const getItemDurationMinutes = (
  startTime: CalendarGridTimeString,
  endTime: CalendarGridTimeString,
): number => {
  return toMinutes(endTime) - toMinutes(startTime);
};

const minutesTo24H = (minutes: number): CalendarGridTimeString => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return (String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')) as CalendarGridTimeString;
};

const minutesTo12H = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const h12 = String(h % 12 === 0 ? 12 : h % 12).padStart(2, '0');
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

export const to12H = (time: CalendarGridTimeString) => {
  return minutesTo12H(toMinutes(time));
};

export const getTimeLabel = (
  startTime: CalendarGridTimeString,
  endTime: CalendarGridTimeString,
  timeMode: CalendarGridTimeMode,
) => {
  return `${timeMode === '12H' ? to12H(startTime) : startTime} - ${
    timeMode === '12H' ? to12H(endTime) : endTime
  }`;
};

/**
 * Merge items that overlap in time, grouped by their time lane.
 * - If `ignoreTimeLane` is true, all items are treated as if they are in the same lane,
 *   and only time overlap is considered.
 * - Overlapping items are combined into a single item that spans from the earliest start
 *   to the latest end, and the original items are listed in `overlappedItems`.
 * - Non-overlapping items are returned unchanged.
 */
export const mergeOverlappingItemsByTimeLane = (
  items: CalendarGridDayTimeLineItem[],
  ignoreTimeLane: boolean,
  onOverlappedProps?: OnOverlappedTimeLineItemProps,
): CalendarGridDayOverlappedTimeLineItem[] => {
  if (!items.length) return [];

  const sorted = [...items].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));

  const byTimeLaneMap = new Map<string, CalendarGridDayTimeLineItem[]>();
  if (ignoreTimeLane) {
    // One group to ignore timeLaneIndex, and find overlapping only
    byTimeLaneMap.set('0', sorted);
  } else {
    // Group by timeLaneIndex
    for (const item of sorted) {
      const key = String(item.timeLaneIndex);
      const list = byTimeLaneMap.get(key);
      if (list) {
        list.push(item);
      } else {
        byTimeLaneMap.set(key, [item]);
      }
    }
  }

  const result: CalendarGridDayOverlappedTimeLineItem[] = [];

  for (const [timeLane, timeLaneItems] of byTimeLaneMap) {
    let clusterStartMinute: number | null = null;
    let clusterEndMinute: number | null = null;
    let cluster: CalendarGridDayTimeLineItem[] = [];

    const flush = () => {
      if (clusterStartMinute === null || clusterEndMinute === null || !cluster.length) {
        return;
      }

      if (cluster.length === 1 && !ignoreTimeLane) {
        result.push(cluster[0]);
      } else {
        const startTime = minutesTo24H(clusterStartMinute);
        const endTime = minutesTo24H(clusterEndMinute);
        result.push({
          startTime,
          endTime,
          id: cluster.map((v) => v.id).join(','),
          timeLaneIndex: parseInt(timeLane, 10),
          overlappedItems: cluster,
          ...(onOverlappedProps ? onOverlappedProps(cluster) : {}),
        });
      }

      clusterStartMinute = null;
      clusterEndMinute = null;
      cluster = [];
    };

    const hasOverlapWithCluster = (startMinute: number) =>
      clusterEndMinute !== null ? startMinute < clusterEndMinute : false;

    const putToCluster = (item: CalendarGridDayTimeLineItem) => {
      const start = toMinutes(item.startTime);
      const end = toMinutes(item.endTime);

      if (clusterStartMinute === null) {
        clusterStartMinute = start;
        clusterEndMinute = end;
        cluster = [item];
      } else if (hasOverlapWithCluster(start)) {
        if (!clusterEndMinute || end > clusterEndMinute) {
          clusterEndMinute = end;
        }
        cluster.push(item);
      } else {
        flush();
        clusterStartMinute = start;
        clusterEndMinute = end;
        cluster = [item];
      }
    };

    for (const item of timeLaneItems) putToCluster(item);
    flush();
  }

  if (ignoreTimeLane) {
    result.forEach((item) => {
      if (!item.overlappedItems?.length) {
        return;
      }
      item.overlappedItems.sort((a, b) => a.timeLaneIndex - b.timeLaneIndex);
    });
  }

  return result;
};

const DAY_IN_MINUTES = toMinutes('23:59');
export const getItemDimensions = (
  startTime: CalendarGridTimeString,
  endTime: CalendarGridTimeString,
  gridStep: number,
): { pos: number; height: number; spaceBelow: number } => {
  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);
  const durationMin = Math.max(0, endMin - startMin);

  const pos = Math.floor(gridStep * (startMin / 60));
  const height = Math.floor(gridStep * (durationMin / 60));
  const verticalMax = Math.floor(gridStep * (DAY_IN_MINUTES / 60));
  const spaceBelow = verticalMax - (pos + height);

  return { pos, height, spaceBelow };
};
